import type { components } from '@shared/api'

export type Exercise = components['schemas']['Exercise']
export type ExerciseMutation = components['schemas']['ExerciseMutation']
export type ExerciseMediaAttachment = components['schemas']['ExerciseMediaMutation']
export type ExercisePage = components['schemas']['ExercisePage']
export type MuscleAssignment = components['schemas']['MuscleAssignment']
export type MuscleGroup = components['schemas']['MuscleGroup']
export type TaxonomyItem = components['schemas']['TaxonomyItem']
export type ValidationReport = components['schemas']['ValidationReport']
export type ValidationIssue = components['schemas']['ValidationIssue']

export type ExerciseTaxonomies = {
	muscles: MuscleGroup[]
	difficulties: TaxonomyItem[]
	equipment: TaxonomyItem[]
	types: TaxonomyItem[]
}

export type ExerciseFormValues = {
	name: string
	description: string
	technique: string
	safety_notes: string
	contraindications: string
	exercise_type: string
	difficulty_code: string
	equipment_codes: string[]
	is_ai_available: boolean
	is_weight: boolean
	default_duration?: number
	default_reps?: number
	default_sets?: number
	default_rest_time?: number
	muscles: MuscleAssignment[]
	media: ExerciseMediaAttachment[]
}

export const normalizeExerciseMedia = (attachments: ExerciseMediaAttachment[]): ExerciseMediaAttachment[] => {
	let primaryAssigned = false
	const normalized = attachments.map((attachment, index) => {
		const isPrimary = Boolean(attachment.is_primary) && !primaryAssigned
		if (isPrimary) primaryAssigned = true
		return {
			asset_id: attachment.asset_id,
			role: attachment.role.trim() || 'content',
			is_primary: isPrimary,
			order: index,
			alt_text: attachment.alt_text?.trim() || undefined,
		}
	})
	if (normalized.length && !primaryAssigned) normalized[0] = { ...normalized[0], is_primary: true }
	return normalized
}

export const emptyExerciseValues = (taxonomies?: ExerciseTaxonomies): ExerciseFormValues => ({
	name: '',
	description: '',
	technique: '',
	safety_notes: '',
	contraindications: '',
	exercise_type: taxonomies?.types.find(item => item.active)?.code ?? 'strength',
	difficulty_code: taxonomies?.difficulties.find(item => item.active)?.code ?? '',
	equipment_codes: taxonomies?.equipment.some(item => item.active && item.code === 'no_equipment') ? ['no_equipment'] : [],
	is_ai_available: false,
	is_weight: false,
	default_duration: undefined,
	default_reps: undefined,
	default_sets: undefined,
	default_rest_time: undefined,
	muscles: [],
	media: [],
})

export const exerciseToFormValues = (exercise: Exercise): ExerciseFormValues => ({
	name: exercise.name,
	description: exercise.description ?? '',
	technique: exercise.technique ?? '',
	safety_notes: exercise.safety_notes ?? '',
	contraindications: exercise.contraindications ?? '',
	exercise_type: exercise.exercise_type ?? '',
	difficulty_code: exercise.difficulty_code ?? '',
	equipment_codes: exercise.equipment_codes ?? [],
	is_ai_available: exercise.is_ai_available ?? false,
	is_weight: exercise.is_weight ?? false,
	default_duration: exercise.default_duration,
	default_reps: exercise.default_reps,
	default_sets: exercise.default_sets,
	default_rest_time: exercise.default_rest_time,
	muscles: (exercise.muscles ?? []).map((muscle, index) => ({
		...muscle,
		order: muscle.order ?? index,
		activation_level: muscle.activation_level ?? 0,
	})),
	media: normalizeExerciseMedia((exercise.media ?? []).map((asset, index) => ({
		asset_id: asset.asset_id,
		role: asset.role || 'content',
		is_primary: asset.is_primary ?? index === 0,
		order: asset.order ?? index,
		alt_text: asset.alt_text,
	})).sort((left, right) => left.order - right.order)),
})

const positiveOrUndefined = (value: number | undefined) =>
	value === undefined || Number.isNaN(value) ? undefined : Math.max(0, Math.trunc(value))

export const formValuesToMutation = (values: ExerciseFormValues): ExerciseMutation => ({
	name: values.name.trim(),
	description: values.description.trim(),
	technique: values.technique.trim(),
	safety_notes: values.safety_notes.trim(),
	contraindications: values.contraindications.trim(),
	exercise_type: values.exercise_type,
	difficulty_code: values.difficulty_code,
	equipment_codes: values.equipment_codes,
	is_ai_available: values.is_ai_available,
	is_weight: values.is_weight,
	default_duration: positiveOrUndefined(values.default_duration),
	default_reps: positiveOrUndefined(values.default_reps),
	default_sets: positiveOrUndefined(values.default_sets),
	default_rest_time: positiveOrUndefined(values.default_rest_time),
	muscles: values.muscles.map((muscle, index) => ({
		muscle_group_id: muscle.muscle_group_id,
		role: muscle.role,
		activation_level: muscle.activation_level ?? 0,
		order: index,
	})),
	media: normalizeExerciseMedia(values.media),
})

export const lifecycleLabel: Record<Exercise['lifecycle'], string> = {
	DRAFT: 'Черновик',
	PUBLISHED: 'Опубликовано',
	ARCHIVED: 'В архиве',
}
